import axios from 'axios'
import { assert } from '../utils/assert'

class VersionService {
  constructor() {
    this.jwt = localStorage.getItem('jwt')
  }

  /**
   * Récupère les statistiques de différences entre les versions
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.language - Langue des versions
   * @param {Array<number>} params.versions - Liste des numéros de versions à comparer
   * @param {boolean} params.latestVersionOnly - Si true, on récupère les statistiques de la dernière version uniquement
   * @param {boolean} params.skipLatestVersion - Si true, on exclue automatiquement la dernière version
   * @returns {Promise<Object>} Statistiques des différences
   */
  async getDiffStats({
    language = null,
    versions = null,
    latestVersionOnly = null,
    skipLatestVersion = null,
  } = {}) {
    const queryParams = {}
    if (language !== null) {
      queryParams.language = language
    }
    if (versions !== null) {
      queryParams.versions = versions
    }
    if (latestVersionOnly !== null) {
      queryParams.latestVersionOnly = latestVersionOnly
    }
    if (skipLatestVersion !== null) {
      queryParams.skipLatestVersion = skipLatestVersion
    }
    try {
      const response = await axios.get('/api/version/data/diff/stats', {
        params: queryParams,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data
    } catch (error) {
      console.error(
        'Erreur lors de la récupération des statistiques de différences:',
        error
      )
      throw error
    }
  }

  /**
   * Récupère les différences entre les versions
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.language - Langue des versions
   * @param {Array<number>} params.versions - Liste des numéros de versions à comparer
   * @param {boolean} params.latestVersionOnly - Si true, on récupère les statistiques de la dernière version uniquement
   * @param {boolean} params.skipLatestVersion - Si true, on exclue automatiquement la dernière version
   * @returns {Promise<Object>} Statistiques des différences
   */
  async getDiff({
    language = null,
    versions = null,
    latestVersionOnly = null,
    skipLatestVersion = null,
  } = {}) {
    const queryParams = {}
    if (language !== null) {
      queryParams.language = language
    }
    if (versions !== null) {
      queryParams.versions = versions
    }
    if (latestVersionOnly !== null) {
      queryParams.latestVersionOnly = latestVersionOnly
    }
    if (skipLatestVersion !== null) {
      queryParams.skipLatestVersion = skipLatestVersion
    }
    try {
      const response = await axios.get('/api/version/data/diff', {
        params: queryParams,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des différences:', error)
      throw error
    }
  }

  async getUnusedBetaVersions({ language = null } = {}) {
    const queryParams = {
      type: 'data',
      sort: 'numberVersion',
      order: 'desc',
    }
    if (language !== null) {
      queryParams.language = language
    }

    try {
      const response = await axios.get('/api/version', {
        params: queryParams,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data.filter((version) => version.preRelease)
    } catch (error) {
      console.error('Erreur lors de la récupération des versions beta:', error)
      throw error
    }
  }

  /**
   * Récupère la liste des versions
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.type - Type de version (data, package)
   * @param {string} params.language - Langue des versions
   * @param {string} params.sort - Champ de tri
   * @param {string} params.order - Ordre de tri (asc, desc)
   * @returns {Promise<Array>} Liste des versions
   */
  async getVersions({
    type = 'data',
    language = null,
    sort = 'numberVersion',
    order = 'desc',
  } = {}) {
    const queryParams = {
      type,
      language,
      sort,
      order,
    }

    try {
      const response = await axios.get('/api/version', {
        params: queryParams,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des versions:', error)
      throw error
    }
  }

  /**
   * Récupère les statistiques des versions
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.language - Langue des versions
   * @returns {Promise<Object>} Statistiques des versions
   */
  async getAnalytics({ language = null } = {}) {
    const queryParams = {}
    if (language !== null) {
      queryParams.language = language
    }

    try {
      const response = await axios.get('/api/version/analytics', {
        params: queryParams,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics:', error)
      throw error
    }
  }

  /**
   * Publie une version beta
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.language - Langue de la version (obligatoire)
   * @returns {Promise<Object>} Réponse de l'API
   */
  async publishDataVersionBeta({ language }) {
    assert(language, 'language is required')

    try {
      const response = await axios.post(
        '/api/version/data/publish/beta',
        {
          language: language,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${this.jwt}`,
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('Erreur lors de la publication de la version beta:', error)
      throw error
    }
  }

  /**
   * Publie une version stable à partir des versions beta sélectionnées
   * @param {Object} params - Paramètres de la requête
   * @param {Array<string>} params.versions - Liste des versions beta qui seront publiées
   * @param {string} params.language - Langue de la version (obligatoire)
   * @returns {Promise<Object>} Réponse de l'API
   */
  async publishDataVersionStable({ versions, language }) {
    assert(versions, 'versions is required')
    assert(Array.isArray(versions), 'versions must be an array')
    assert(versions.length > 0, 'versions must not be empty')
    assert(language, 'language is required')

    try {
      const response = await axios.post(
        '/api/version/data/publish/stable',
        {
          versions,
          language,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${this.jwt}`,
          },
        }
      )
      return response.data
    } catch (error) {
      console.error(
        'Erreur lors de la publication de la version stable:',
        error
      )
      throw error
    }
  }

  /**
   * Récupère les différences pour une version spécifique
   * @param {number} versionNumber - Numéro de la version
   * @returns {Promise<Object>} Différences de la version
   */
  async getDiffForVersion(versionNumber) {
    assert(versionNumber, 'versionNumber is required')

    try {
      const response = await axios.get('/api/version/data/diff', {
        params: {
          versions: [versionNumber],
        },
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${this.jwt}`,
        },
      })
      return response.data
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Erreur lors de la récupération des différences pour la version:',
        error
      )
      throw error
    }
  }

  /**
   * Génère l'URL de téléchargement pour une version
   * @param {string} versionId - ID de la version
   * @returns {string} URL de téléchargement
   */
  getDownloadUrl(versionId) {
    assert(versionId, 'versionId is required')

    const baseUrl =
      process.env.NODE_ENV === 'development' ? 'http://localhost:8090' : ''
    return `${baseUrl}/api/version/download/${versionId}`
  }

}

export default new VersionService()
